import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { map, Observable, of, switchMap } from 'rxjs';
import { Globals } from '@aiwyn/config';
import { API_BASE_PATH } from '@aiwyn/api';
import { CxpEsignDetail, CxpEsignSigner } from '../models';

/**
 * Reads the eSign detail page needs, from the client-facing eSign API.
 *
 * Two calls, because the route only knows the CLIENT REQUEST id: the list
 * resolves that to the bundle the caller is a recipient of, and the signing
 * context supplies the document and the roster. Both are scoped server-side to
 * the caller's own contact — no id from this client widens what comes back.
 *
 * Owned by the CXP module rather than shared with the secure-link feature: that
 * one speaks for a link-bearing guest, this one for a portal session, and the
 * two authenticate differently even though they call the same paths.
 */
@Injectable({ providedIn: 'root' })
export class CxpEsignApiService {
  private readonly http = inject(HttpClient);
  private readonly globals = inject(Globals);

  private get base(): string {
    return `${this.globals.artUrl}${API_BASE_PATH}/client/esign/v1`;
  }

  /**
   * Resolves one client request to its eSign detail, or null when the caller
   * holds no recipient row on it — which is what a contact who can see the CRL
   * but was never made a signer looks like.
   *
   * `recipientId` says which signature, for the request that owes the same
   * person two — the caller reads it from the URL the task card wrote.
   */
  getDetailForClientRequest(clientRequestId: number, recipientId?: number | null): Observable<CxpEsignDetail | null> {
    return this.listSignatureRequests().pipe(
      map((items) => selectSignatureRow(items, clientRequestId, recipientId)),
      switchMap((request) => (request ? this.withSigningContext(request) : of(null))),
    );
  }

  /**
   * Every signature the calling contact owes, one entry per recipient row — so a
   * bundle addressing the same person twice yields two. The task list expands its
   * eSign rows from this; the detail page reads it to find one bundle.
   */
  listSignatureRequests(): Observable<EsignSignatureRequestRow[]> {
    return this.http
      .get<EsignSignatureRequestListResponse>(`${this.base}/signature-requests`)
      .pipe(map((response) => response.items ?? []));
  }

  /**
   * The signing context is scoped to one recipient row, and the row resolved above names itself —
   * so it is sent every time rather than only when the caller asked for a particular signature.
   * Naming only the bundle is ambiguous for a contact holding two rows on it, which the API refuses
   * with the conflict that tells the signer to use their emailed link.
   */
  private withSigningContext(request: EsignSignatureRequestRow): Observable<CxpEsignDetail> {
    const params = new HttpParams().set('bundleId', request.bundleId).set('recipientId', request.recipientId);
    return this.http
      .get<EsignSigningContextResponse>(`${this.base}/signing-context`, { params })
      .pipe(map((context) => toDetail(request, context)));
  }
}

/**
 * Which of the caller's rows a page is about. A request owing the same person two signatures yields
 * two rows, and the id from the URL is the only thing that tells them apart; a link naming none — or
 * naming one the caller does not hold — still opens, on the first row rather than on nothing.
 */
function selectSignatureRow(
  items: EsignSignatureRequestRow[],
  clientRequestId: number,
  recipientId?: number | null,
): EsignSignatureRequestRow | null {
  const onRequest = items.filter((item) => item.clientRequestId === clientRequestId);
  return onRequest.find((item) => item.recipientId === recipientId) ?? onRequest[0] ?? null;
}

function toDetail(request: EsignSignatureRequestRow, context: EsignSigningContextResponse): CxpEsignDetail {
  const name = context.bundle?.name ?? request.bundleName;
  return {
    clientRequestId: request.clientRequestId ?? 0,
    bundleId: request.bundleId,
    recipientId: request.recipientId,
    name,
    signerDisplayName: request.signerDisplayName ?? null,
    state: request.signed ? 'completed' : 'required',
    // The signing context carries the document by URL, not by name — the same
    // `{bundle}.pdf` the secure-link viewer already labels its download with.
    filename: name ? `${name}.pdf` : null,
    signedAt: request.signedAndAcceptedAt ?? null,
    signedFileUrl: context.file?.signedFileUrl ?? null,
    signers: (context.signers ?? []).map(toSigner),
  };
}

/**
 * The roster has no stable id — the projection is deliberately narrow — so the
 * track key is built from the fields it does carry plus the row's position.
 */
function toSigner(signer: EsignSigningContextSigner, index: number): CxpEsignSigner {
  return {
    key: `${index}-${signer.displayName}`,
    displayName: signer.displayName,
    email: signer.email ?? null,
    signed: signer.status === 'SIGNED',
  };
}

// --- wire contracts ----------------------------------------------------------
//
// Mirror the artengine responses exactly; the FE model above is what the page
// consumes, so a backend rename lands here rather than in the components.

interface EsignSignatureRequestListResponse {
  items: EsignSignatureRequestRow[];
}

export interface EsignSignatureRequestRow {
  recipientId: number;
  bundleId: number;
  bundleName: string;
  clientRequestId: number | null;
  signerDisplayName: string;
  status: string;
  signed: boolean;
  awaitingOtherSigners: boolean;
  dueDate?: string | null;
  signedAndAcceptedAt?: string | null;
}

interface EsignSigningContextResponse {
  bundle?: { id: number; name: string; status: string };
  file?: { originalFileUrl: string; combinedXfdf?: string | null; signedFileUrl?: string | null };
  signers?: EsignSigningContextSigner[];
}

interface EsignSigningContextSigner {
  displayName: string;
  status: string;
  /** Added by ART-8803 so the roster can render the address the Figma shows. */
  email?: string | null;
}
